import { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { Button } from "~/components";
import { ROUTES } from "~/router";
import AuthBanner, { BannerTypes } from "~/screens/Auth/components/AuthBanner";
import BackButton from "~/screens/Auth/components/BackButton";
import { OtpInput } from "~/screens/Auth/components/OtpInput";
import { ResendEmail } from "~/screens/Auth/components/ResendEmail";
import { useSendOtp } from "~/screens/Auth/hooks/useSendOtp";
import { useVerifyOtp } from "~/screens/Auth/hooks/useVerifyOtp";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { Title, usePageTitle } from "~/shared";
import { validateError } from "~/utils";

export const CodeValidation = () => {
  usePageTitle("Enter your code");

  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const email: string = state?.email ?? "";

  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const code = otp.join("");
  const isComplete = otp.every((d) => d !== "");

  const {
    mutate: sendOtp,
    isPending: isSendingOtp,
    isSuccess: otpSent,
  } = useSendOtp();

  const { mutate: verifyOtp, isPending, error, reset } = useVerifyOtp();

  const handleSubmit = () => {
    verifyOtp({
      email,
      code,
      clientId: searchParams.get("client_id"),
      redirectUri: searchParams.get("redirect_uri"),
      responseType: searchParams.get("response_type"),
      scope: searchParams.get("scope"),
      state: searchParams.get("state"),
    });
  };

  const onInputChange = (newOtp: string[]) => {
    reset();
    setOtp(newOtp);
  };

  useEffect(() => {
    if (isComplete) {
      handleSubmit();
    }
  }, [isComplete]);

  if (!email) {
    return <Navigate to={ROUTES.fullLogin} />;
  }

  return (
    <AuthLayout
      banner={<AuthBanner type={BannerTypes.codeValidation} />}
      canGoBack
      buttonClassName="!hidden"
      showSupport
    >
      <section className="flex w-full items-center justify-center">
        <div className="relative w-full max-w-lg space-y-6">
          <BackButton variant="violet" className="m-0 hidden md:flex" />

          <div className="space-y-3">
            <Title>Enter your code</Title>
            <p className="font-sans text-blue-900">
              A 6-digit code has been sent to {email} if an account exists.
            </p>
          </div>

          <OtpInput
            otp={otp}
            onChange={onInputChange}
            error={validateError(error)?.response.data.error.message}
          />

          <Button
            size="default"
            type="button"
            onClick={handleSubmit}
            disabled={!isComplete || isPending}
            className="w-full"
          >
            Continue
          </Button>

          <ResendEmail
            isPending={isSendingOtp}
            isSuccess={otpSent}
            onClick={() => sendOtp({ email, searchParams })}
          />
        </div>
      </section>
    </AuthLayout>
  );
};
